import { StatusBadgeChip } from '@/components/common';
import { type OKR } from '@/domain/okrs/type';
import { useProductRoadmap } from '@/domain/products/hooks';
import { type Product, type ProductRoadmap } from '@/domain/products/types';
import { RoadMap } from '@/domain/roadmap/types';
import { getImpact, getImpactColor } from '@/features/roadmap/utils';
import { toDateString } from '@/utils/datetime';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { DataGrid, GridColDef, GridRenderCellParams } from '@andritzot/metris-web-ui/mui-x/data-grid';

type Row = (RoadMap | ProductRoadmap) & { product: Product };

const OKRsBodyTemplate = ({ okrs }: { okrs: OKR[] }) => (
  <Box>
    {[...okrs]
      .sort((a, b) => b.impact - a.impact)
      .map(({ id, alias, impact, description, name }) => (
        <Tooltip
          key={id}
          title={
            <Box>
              <Box>
                <Typography component="span" sx={{ mr: 0.5 }}>
                  ({getImpact(impact)})
                </Typography>
                <Typography component="span">{name}</Typography>
              </Box>
              <Typography component="span">{description}</Typography>
            </Box>
          }
        >
          <Box sx={{ width: '100%' }}>
            <StatusBadgeChip color={getImpactColor(impact)}>{alias ?? name}</StatusBadgeChip>
          </Box>
        </Tooltip>
      ))}
  </Box>
);

const releaseDateTemplate = (params: GridRenderCellParams<Row>) => {
  const rowData = params.row;
  return rowData.version?.releaseDate ? toDateString(rowData.version.releaseDate) : '';
};

const statusBodyTemplate = (params: GridRenderCellParams<Row>) =>
  params.row.status ? `${params.row.status?.name} (${Math.round(params.row.percentComplete)}%)` : '';

interface RoadmapTabProps {
  product: Product;
}

const RoadmapTab = ({ product }: RoadmapTabProps) => {
  const { data = [], isLoading } = useProductRoadmap(product.idProduct);

  const gridData: Row[] = data.map((r) => ({ ...r, product }));

  const columns: GridColDef<Row>[] = [
    {
      field: 'type.name',
      headerName: 'Type',
      sortable: true,
      width: 130,
      valueGetter: (_value, row) => row.type?.name ?? '',
    },
    {
      field: 'item',
      headerName: 'Item',
      sortable: true,
      flex: 2,
      minWidth: 160,
    },
    {
      field: 'version.name',
      headerName: 'Version',
      sortable: true,
      width: 80,
      valueGetter: (_value, row) => row.version?.name ?? '',
    },
    {
      field: 'okrs.name',
      headerName: 'Business Impact',
      sortable: false,
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <OKRsBodyTemplate okrs={params.row.okrs} />,
    },
    {
      field: 'version.releaseDate',
      headerName: 'Date',
      sortable: true,
      width: 110,
      valueGetter: (_value, row) => row.version?.releaseDate ?? '',
      renderCell: (params) => releaseDateTemplate(params),
    },
    {
      field: 'percentComplete',
      headerName: 'Status',
      sortable: true,
      width: 120,
      renderCell: (params) => statusBodyTemplate(params),
    },
  ];

  return (
    <DataGrid
      loading={isLoading}
      rows={gridData}
      columns={columns}
      getRowId={(row: Row) => row.idRoadmap}
      pagination
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
    />
  );
};

export default RoadmapTab;
